
import {
  computed, defineComponent, reactive,
} from 'vue';
import { useRouter } from 'vue-router';
import AppCaseViewPopUp from '@/components/AppCaseViewPopUp.vue';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import {
  ICase, IConversationMessage, IFolder, IMedia,
} from '@/types/interfaces';
import {
  lowResUrlByViewType,
  lowResUrlByViewTypeByUrl,
} from '@/helpers/lowResPhotoUrl';
import { useMainStore } from '@/store/mainStore';
import { MEDIA_TYPES, PHOTO_VIEW_TYPE, SHARE_SETTINGS } from '@/types/enums';
import AppLazyImage from '@/components/UI/AppLazyImage.vue';
import { descriptionWithLink } from '@/helpers/utils';

export default defineComponent({
  props: {
    msg: { type: Object as () => IConversationMessage, required: true },
    isGroup: { type: Boolean, default: false },
  },
  components: {
    AppLazyImage,
    AppCaseViewPopUp,
  },
  setup(props) {
    const router = useRouter();
    const driveStore = useDriveStore();
    const mainStore = useMainStore();

    const previewCase = reactive({
      id: '', title: '', photosAmount: 0, filesAmount: 0, show: false,
    });

    const isMy = computed(() => props.msg.owner.accountId === mainStore.user.accountId);
    const reshareAllowed = computed(() => props.msg.shareSettings?.includes(SHARE_SETTINGS.RESHARE_ALLOWED));

    const photo = computed(() => {
      if ((props.msg.attachment as IMedia)?.type === MEDIA_TYPES.PHOTO) {
        return {
          ...props.msg.attachment as IMedia,
          shareSettings: props.msg.shareSettings,
          root: true,
        };
      }
      return null;
    });
    const file = computed(() => {
      if ((props.msg.attachment as IMedia)?.type === MEDIA_TYPES.FILES) {
        return {
          ...props.msg.attachment as IMedia,
          shareSettings: props.msg.shareSettings,
          root: true,
        };
      }
      return null;
    });
    const video = computed(() => {
      if ((props.msg.attachment as IMedia)?.type === MEDIA_TYPES.VIDEO) {
        return {
          ...props.msg.attachment as IMedia,
          shareSettings: props.msg.shareSettings,
          root: true,
        };
      }
      return null;
    });
    const iCase = computed(() => {
      if ((props.msg.attachment as ICase)?.title) {
        return {
          ...props.msg.attachment as ICase,
          shareSettings: props.msg.shareSettings,
          root: true,
        };
      }
      return null;
    });
    const folder = computed(() => ((props.msg.attachment as IFolder).folderType ? props.msg.attachment as IFolder : null));

    const photoUrl = computed(() => {
      if (props.msg.owner.photo?.url) {
        return lowResUrlByViewTypeByUrl(props.msg.owner.photo?.url, PHOTO_VIEW_TYPE.WHSML);
      }
      // eslint-disable-next-line global-require
      return require('@/assets/img/mockup/profile.svg');
    });

    function resolveTime(time: string) {
      return new Date(time).toLocaleString('en-US', { hour: 'numeric', hour12: false, minute: 'numeric' });
    }

    function viewCase() {
      previewCase.title = iCase.value!.title;
      previewCase.photosAmount = iCase.value!.photosAmount!;
      previewCase.filesAmount = iCase.value!.filesAmount!;
      previewCase.id = props.msg.attachment!.id!;
      previewCase.show = true;
    }

    function viewFolder() {
      if (isMy.value) {
        router.push(`/drive/folder/${props.msg.attachment!.id}/`);
      } else {
        router.push(`/drive/network/folder/${props.msg.attachment!.id}/`);
      }
    }

    function openMedia() {
      driveStore.appPhotoViewMedia = photo.value!;
      // driveStore.fullscreenPhoto = photo.value!;=p
    }

    function openFile() {
      driveStore.appPhotoViewMedia = file.value! || video.value;
      // driveStore.fullscreenFile = file.value! || video.value;
    }

    const caseMedia = computed(() => {
      if (iCase.value) {
        return [...iCase.value.videos, ...iCase.value.photos, ...iCase.value.files];
      }
      return [];
    });

    const casePhoto1 = computed(() => {
      if (caseMedia.value[0]?.type === MEDIA_TYPES.VIDEO) {
        return lowResUrlByViewType(caseMedia.value[0], PHOTO_VIEW_TYPE.THUMB);
      }
      if (caseMedia.value[0]?.type === MEDIA_TYPES.PHOTO) {
        return lowResUrlByViewType(caseMedia.value[0], PHOTO_VIEW_TYPE.WHMED);
      }
      if (caseMedia.value[0]?.type === MEDIA_TYPES.FILES) {
        // eslint-disable-next-line global-require
        return require('@/assets/img/graphic/file.svg');
      }
      return '';
    });

    const casePhoto2 = computed(() => {
      if (caseMedia.value[1]?.type === MEDIA_TYPES.VIDEO) {
        return lowResUrlByViewType(caseMedia.value[1], PHOTO_VIEW_TYPE.THUMB);
      }
      if (caseMedia.value[1]?.type === MEDIA_TYPES.PHOTO) {
        return lowResUrlByViewType(caseMedia.value[1], PHOTO_VIEW_TYPE.WHMED);
      }
      if (caseMedia.value[1]?.type === MEDIA_TYPES.FILES) {
        // eslint-disable-next-line global-require
        return require('@/assets/img/graphic/file.svg');
      }
      return '';
    });

    const casePhoto3 = computed(() => {
      if (caseMedia.value[2]?.type === MEDIA_TYPES.VIDEO) {
        return lowResUrlByViewType(caseMedia.value[2], PHOTO_VIEW_TYPE.THUMB);
      }
      if (caseMedia.value[2]?.type === MEDIA_TYPES.PHOTO) {
        return lowResUrlByViewType(caseMedia.value[2], PHOTO_VIEW_TYPE.WHMED);
      }
      if (caseMedia.value[2]?.type === MEDIA_TYPES.FILES) {
        // eslint-disable-next-line global-require
        return require('@/assets/img/graphic/file.svg');
      }
      return '';
    });

    return {
      photoUrl,
      isMy,
      photo,
      file,
      iCase,
      video,
      folder,
      casePhoto1,
      casePhoto2,
      casePhoto3,
      caseMedia,
      resolveTime,
      viewCase,
      viewFolder,
      openMedia,
      openFile,
      lowResUrlByViewType,
      lowResUrlByViewTypeByUrl,
      PHOTO_VIEW_TYPE,
      previewCase,
      reshareAllowed,
      descriptionWithLink,
    };
  },
});
